// Glacier Copilot API Authentication Utility

// Use proxy in development, direct URL in production
const isDevelopment = process.env.NODE_ENV === 'development';
const COPILOT_API_BASE = isDevelopment 
    ? '/copilot-api' // Use proxy in development
    : (process.env.REACT_APP_COPILOT_API_BASE || 'https://copilot.glacies.ai');

const COPILOT_AUTH_KEY = 'glacier_copilot_auth';
const COPILOT_USER_KEY = 'glacier_copilot_user';

// Temporary credentials for demo
const DEMO_CREDENTIALS = {
    email: 'elizabeth.parrot@example.com',
    password: 'Elizabeth123!'
};

console.log('Copilot API Base:', COPILOT_API_BASE, 'isDevelopment:', isDevelopment);

export const glacierCopilotAuth = {
    // Check if user is authenticated with Copilot API
    isAuthenticated: () => {
        const authData = localStorage.getItem(COPILOT_AUTH_KEY);
        if (!authData) return false;
        
        try {
            const { timestamp } = JSON.parse(authData);
            // Check if session is less than 24 hours old
            const isValid = Date.now() - timestamp < 24 * 60 * 60 * 1000;
            return isValid;
        } catch {
            return false;
        }
    },

    // Get stored user data
    getUser: () => {
        const userData = localStorage.getItem(COPILOT_USER_KEY);
        if (!userData) return null;
        
        try {
            return JSON.parse(userData);
        } catch {
            return null;
        }
    },

    // Login to Copilot API
    login: async (email = DEMO_CREDENTIALS.email, password = DEMO_CREDENTIALS.password) => {
        try {
            const res = await fetch(`${COPILOT_API_BASE}/api/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include', // Important for cookies
                body: JSON.stringify({ email, password })
            });

            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.error || `Login failed (${res.status})`);
            }

            // Store authentication timestamp
            localStorage.setItem(COPILOT_AUTH_KEY, JSON.stringify({
                timestamp: Date.now(),
                email
            }));

            // Fetch user info immediately after login
            const userInfo = await glacierCopilotAuth.fetchUserInfo();
            return userInfo;
        } catch (error) {
            console.error('Copilot login error:', error);
            throw error;
        }
    },

    // Fetch current user info
    fetchUserInfo: async () => {
        try {
            const res = await fetch(`${COPILOT_API_BASE}/api/auth/me`, {
                credentials: 'include'
            });

            if (!res.ok) {
                throw new Error('Failed to fetch user info');
            }

            const data = await res.json();
            if (data && data.authenticated && data.user) {
                localStorage.setItem(COPILOT_USER_KEY, JSON.stringify(data.user));
                return data.user;
            }

            throw new Error('Not authenticated');
        } catch (error) {
            console.error('Fetch user info error:', error);
            throw error;
        }
    },

    // Logout from Copilot API
    logout: async () => {
        try {
            await fetch(`${COPILOT_API_BASE}/api/auth/logout`, {
                method: 'POST',
                credentials: 'include'
            }).catch(() => {});
        } finally {
            localStorage.removeItem(COPILOT_AUTH_KEY);
            localStorage.removeItem(COPILOT_USER_KEY);
        }
    },

    // Ensure authenticated (login if needed)
    ensureAuthenticated: async () => {
        // First check if already authenticated
        try {
            const userInfo = await glacierCopilotAuth.fetchUserInfo();
            return userInfo;
        } catch {
            // Not authenticated, try to login with demo credentials
            console.log('Not authenticated, logging in with demo credentials...');
            return await glacierCopilotAuth.login();
        }
    },

    // Get API base URL
    getApiBase: () => COPILOT_API_BASE
};

export default glacierCopilotAuth;
